"use client"

import type { ComponentProps } from "react"

import { SectionHeader, SeeAllButton } from "@/components/marketplace/layout/section-header"
import { ProductCard } from "@/components/marketplace/product/product-card"
import { RailArrows } from "@/components/marketplace/product/rail-arrows"
import type { Product } from "@/components/marketplace/types"
import { WindowedReel } from "@/components/ui"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"

interface ProductCarouselProps {
  title: string
  accent?: string
  eyebrow?: string
  products: Product[]
  seeAllHref?: string
  headerSize?: ComponentProps<typeof SectionHeader>["size"]
  numeral?: string
  // "default" is the full-body rail (~1.5-up mobile). "compact" is a denser
  // browse rail — a smaller card that peeks ~3.5-up on mobile and scales back to
  // a comfortable ~6-up on desktop so it never turns tiny.
  variant?: "default" | "compact"
  chrome?: "feed" | "overlay"
}

const DISC_TOP = { compact: "top-32.5 @3xl:top-38.5", feed: "top-30.5 @3xl:top-33", overlay: "top-27.5 @3xl:top-33" }

const REEL_CLASS = { compact: "pb-100 -mb-75 @3xl:gap-350 @3xl:-mb-100", default: "pb-100 -mb-75 @2xl:gap-500 @3xl:-mb-100" }

// One rail, one card width. The default card is a shadowed packshot tile — a
// medium slot that peeks ~2-up on mobile (w-44) and settles to ~6-up on desktop
// (w-48). The compact card is a denser browse slot: ~144px on mobile up to 224px
// on desktop.
export const ProductCarousel = ({ title, accent, eyebrow, products, seeAllHref, headerSize, numeral, variant = "default", chrome = "feed" }: ProductCarouselProps) => {
  const { ref, overflowing, atStart, atEnd, page } = useReelPagination(products.length)
  const compact = variant === "compact"
  return (
    // The rail owns an @container so its @3xl card-width / gap / arrow steps resolve
    // against the rail on the feed page (which otherwise has no container context —
    // @3xl would silently never fire and cards would stay at the mobile width).
    <div className="@container">
      <SectionHeader
        title={title}
        accent={accent}
        eyebrow={eyebrow}
        size={headerSize}
        numeral={numeral}
        action={chrome === "feed" ? <SeeAllButton href={seeAllHref} /> : undefined}
        className="px-gut mb-850 @3xl:mb-750"
      />
      <div className="relative">
        <WindowedReel
          windowSize={chrome === "overlay" ? products.length : compact ? 6 : 4}
          ref={ref}
          gap={compact ? "300" : "250"}
          snap="proximity"
          inset
          className={REEL_CLASS[compact ? "compact" : "default"]}
        >
          {products.map((product) => (
            <div key={product.id} className={compact ? "w-36 @3xl:w-56" : "@2xl:w-rail-card w-34"}>
              <ProductCard product={product} size={compact ? "compact" : "default"} />
            </div>
          ))}
        </WindowedReel>
        {overflowing && <RailArrows atStart={atStart} atEnd={atEnd} onPage={page} top={DISC_TOP[compact ? "compact" : chrome]} />}
      </div>
    </div>
  )
}
